"use client";
import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";

import { adCards, heroStats } from "./data";
import AdCard from "./AdCard";

const EASE = [0.22, 1, 0.36, 1];

// parent only orchestrates the stagger; each card animates itself
const fanGroup = {
  hidden: {},
  visible: {},
};

// Only the y offsets change with screen size. `hidden` is the stacked-low
// start; `side`/`center` are the final fanned positions.
const Y = {
  base: { hidden: 90, side: -8, center: -48 },
  small: { hidden: 90, side: 40, center: 20 },
};

// hidden = stacked in the center, sitting low (only tops peek out), no rotation
// visible = final fanned/spread position
const buildCardVariants = (y) => ({
  left: {
    hidden: { opacity: 0.4, x: 250, y: y.hidden, rotate: 0 },
    visible: {
      opacity: 1,
      x: 12,
      y: y.side,
      rotate: -18,
      transition: { duration: 0.7, ease: EASE },
    },
  },
  center: {
    hidden: { opacity: 1, x: 0, y: y.hidden, scale: 1.06 },
    visible: {
      opacity: 1,
      x: 0,
      y: y.center,
      scale: 1.06,
      transition: { duration: 0.7, ease: EASE },
    },
  },
  right: {
    hidden: { opacity: 0.4, x: -250, y: y.hidden, rotate: 0 },
    visible: {
      opacity: 1,
      x: -24,
      y: y.side,
      rotate: 18,
      transition: { duration: 0.7, ease: EASE },
    },
  },
});

// Fires only when the breakpoint is crossed — not on every resize.
function useIsSmall(query = "(max-width: 768px)") {
  const [isSmall, setIsSmall] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const update = () => setIsSmall(mql.matches);
    update();
    mql.addEventListener("change", update);
    return () => mql.removeEventListener("change", update);
  }, [query]);
  return isSmall;
}

export default function Hero() {
  const isSmall = useIsSmall();
  // Rebuilds only when the breakpoint flips
  const cards = useMemo(
    () => buildCardVariants(isSmall ? Y.small : Y.base),
    [isSmall],
  );

  return (
    <section
      className="relative w-full overflow-hidden"
      style={{
        backgroundColor: "#ffffff",
        backgroundImage: `
    radial-gradient(
      60% 80% at 50% -10%,
      rgba(127, 161, 255, 0.2) 0%,
      rgba(127, 161, 255, 0) 60%
    ),
    repeating-linear-gradient(
      to right,
      rgba(102, 102, 102, 0.13) 0 1px,
      transparent 1px 76px
    ),
    repeating-linear-gradient(
      to bottom,
      rgba(102, 102, 102, 0.13) 0 1px,
      transparent 1px 76px
    )
  `,

        backgroundRepeat: "no-repeat",
        backgroundPosition: "center -10px, 0 -10px, 0 -1px",
      }}
    >
      <div className="px-6 relative md:px-[40px] xl:px-20 pt-12 lg:pt-20">
        {/* heading */}
        <div className=" absolute top-3/4 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[1] w-[1400px] h-[600px] mt-10 bg-white rounded-full blur-[107.63px]" />
        <div className="relative flex flex-col items-center text-center z-20">
          <div className="flex items-center gap-2 border pl-0.5 pr-2 mb-8 mt-4 py-0.5 rounded-full border-[#1414141A]/10 bg-white">
            <span>
              <svg
                width="28"
                height="28"
                viewBox="0 0 28 28"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M14.0026 2.33398C7.5591 2.33398 2.33594 7.55715 2.33594 14.0007C2.33594 19.8235 6.60244 24.65 12.1803 25.5261V17.3723H9.21694V14.0007H12.1803V11.4305C12.1803 8.50682 13.9209 6.89215 16.5868 6.89215C17.8631 6.89215 19.1978 7.11965 19.1978 7.11965V9.98965H17.7278C16.2776 9.98965 15.8261 10.8892 15.8261 11.812V14.0007H19.0613L18.5444 17.3723H15.8261V25.5261C21.4028 24.6511 25.6693 19.8223 25.6693 14.0007C25.6693 7.55715 20.4461 2.33398 14.0026 2.33398Z"
                  fill="#1877F2"
                />
              </svg>
            </span>
            <span className=" text-[#292929] text-[12px] font-medium ">
              Meta Ads Platform
            </span>
          </div>
          <h1 className="w-full max-w-[820px] 2xl:max-w-[960px] solution-heading">
            AI Copy Tools for{" "}
            <span className="font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#0082FB] to-[#0064E0] ">
              Meta
            </span>
          </h1>
          <p className="mt-[18px]  w-full md:max-w-[300px] lg:max-w-[500px] xl:max-w-[600px] 2xl:max-w-[1000px] body-secondary-text">
            Generate scroll-stopping primary text, headlines, and CTAs optimized
            for feeds, stories, and reels. Instantly.{" "}
          </p>

          {/* CTAs */}
          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://app.imagive.ai?redirect=/users/white-chat-board"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center justify-center"
            >
              Generate Meta Copy Now
            </a>
            <a
              href="https://app.imagive.ai?redirect=/users/white-chat-board"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary inline-flex items-center justify-center"
            >
              No Credit Card Required
            </a>
          </div>
        </div>
      </div>

      {/* fanned ad cards */}
      <div className="relative mt-28 md:mt-44">
        {/* desktop fan */}
        <div className=" absolute top-3/4 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[1] w-[400px] h-[500px] bg-[#6366F12E] rounded-full blur-[107.63px]" />
        <motion.div
          variants={fanGroup}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="flex gap-10 items-end justify-center"
        >
          <motion.div
            variants={cards.left}
            whileHover={{ y: -40 }}
            className="z-10"
          >
            <AdCard {...adCards[0]} />
          </motion.div>
          <motion.div
            variants={cards.center}
            whileHover={{ y: -64 }}
            className="relative z-20"
          >
            <AdCard {...adCards[1]} />
          </motion.div>
          <motion.div
            variants={cards.right}
            whileHover={{ y: -40 }}
            className="z-10"
          >
            <AdCard {...adCards[2]} />
          </motion.div>
        </motion.div>
      </div>

      {/* stats bar */}
      <div className="relative z-30 bg-white border-t border-b border-[#F0EEEC] lg:-mt-16">
        <div className="px-6 md:px-[40px] xl:px-20">
          <div className="grid grid-cols-2 lg:grid-cols-4">
            {heroStats.map((s, i) => (
              <div
                key={s.title}
                className={`flex flex-col py-8  lg:my-4 lg:py-10 px-4 lg:px-8 border-[#F0EEEC]
                   ${i !== 0 ? "lg:border-l" : ""} 
                ${i % 2 === 1 ? "border-l" : ""}
${i >= 2 ? "border-t lg:border-t-0 pt-8" : ""}
                `}
              >
                <span className="bg-gradient-to-r from-[#3232AA] via-[#6767C7] to-[#8E8ECD] bg-clip-text text-transparent text-[34px] lg:text-[40px] font-[600] leading-none">
                  {s.value}
                </span>
                <span className="text-[14px] mt-1 font-semibold text-[#1C1917]">
                  {s.title}
                </span>
                <span className="text-[12px] text-[#A8A29E]">{s.sub}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
